// English Investigations & CAPA Management page, built on the Inspection Readiness / QMS design family.
// Icons are always bare (no circle or tinted shape); the Why QbD Group block never carries a photo.
import { Link, Navigate } from "@/lib/router-compat";
import { AlertTriangle, ArrowDown, ArrowRight, CalendarDays, CheckCircle2, Globe, ListChecks, Search, ShieldCheck, TrendingUp } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import CommercialBrowseSidebar from "@/components/services/CommercialBrowseSidebar";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import ContactCTA from "@/components/ContactCTA";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import { Button } from "@/components/ui/button";
import { getServiceBySlug, type ServiceData } from "@/data/servicesData";
import ServiceHero from "@/components/services/ServiceHero";
import { cn } from "@/lib/utils";
import Eyebrow from "@/components/services/SectionEyebrow";
import heroImg from "@/assets/services/capa/hero.jpg";
import challengeImg from "@/assets/services/capa/challenge.jpg";
import cardSupportImg from "@/assets/services/capa/card-support.jpg";
import cardBacklogImg from "@/assets/services/capa/card-backlog.jpg";
import cardMaturityImg from "@/assets/services/capa/card-maturity.jpg";
import approachImg from "@/assets/services/capa/approach.jpg";
import webinarAsset from "@/assets/services/capa/webinar.jpg.asset.json";
import webinarImg from "@/assets/services/capa/webinar.jpg";

export const CAPA_PATH = "/en/services/quality-assurance/quality-compliance-remediation-capa";
export const CAPA_META_TITLE = "Quality Compliance, Remediation & CAPA Management | QbD Group";
export const CAPA_META_DESCRIPTION =
  "Hands-on investigation and CAPA support, CAPA backlog recovery and CAPA maturity programs for GxP-regulated pharma, biotech and MedTech companies.";
export const CAPA_HERO_IMAGE =
  "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public/service-images/quality-assurance/investigations-capa-management.jpg";
const WEBINAR_PATH = "/en/webinars/from-backlog-to-control-capa-maturity-inspection-readiness";
const STORAGE = "https://jrtdedcfhvzaomneervp.supabase.co/storage/v1/object/public";
const WEBINAR_IMAGE = webinarAsset.url;
const WEBINAR_SPEAKERS = [
  { name: "Björn Aalbers", img: `${STORAGE}/avatars/bjorn-aalbers.png` },
  { name: "Monique Eilander", img: `${STORAGE}/avatars/monique-eilander.png` },
  { name: "Bas Michielsen", img: `${STORAGE}/avatars/bas-michielsen.png` },
];

const WARNING_SIGNS = [
  "Growing number of open CAPAs",
  "Investigations overdue by more than 30 days",
  "Repeated due-date extensions",
  "Recurring deviations",
  "Weak root cause conclusions",
  "Incomplete effectiveness checks",
  "Aged complaints or OOS investigations",
  "CAPAs closed without demonstrated effectiveness",
];

const CARDS = [
  {
    icon: Search, title: "Investigation & CAPA support", img: cardSupportImg,
    alt: "An expert in a lab coat inspecting an equipment component with a magnifier alongside a site engineer",
    text: "Hands-on support across the full process: intake and triage of deviations, complaints and audit or inspection findings, investigation and root cause analysis, action planning, execution support and effectiveness review. Our experts are matched to the technical issue, with on-site support where needed.",
  },
  {
    icon: ListChecks, title: "CAPA backlog recovery", img: cardBacklogImg,
    alt: "A quality team sorting color-coded cards into columns on a wall board to prioritize open work",
    text: "A remediation plan to prioritize and coordinate the completion of open investigations and CAPAs: from backlog inventory and risk-based triage to segmentation and a structured recovery plan.",
  },
  {
    icon: TrendingUp, title: "CAPA maturity program", img: cardMaturityImg,
    alt: "A quality manager presenting a management dashboard with charts to a small leadership team",
    text: "A compliance and quality improvement program that strengthens the ongoing control of investigations and CAPAs, so the backlog is less likely to build up again.",
  },
];

const PHASES: { title: string; text: string; tags?: string[]; note?: string }[] = [
  {
    title: "Stabilize",
    text: "Backlog inventory and data clean-up, and risk-based triage based on:",
    tags: ["Product and patient safety", "Data integrity risk", "Recurring failures", "Customer impact", "Regulatory commitments"],
  },
  {
    title: "Reduce",
    text: "Segmentation of open records, an investigation quality review and a CAPA recovery plan that reduces the backlog without compromising quality. Open records are segmented into:",
    tags: ["Quick closures", "Full reinvestigations", "Product impact assessments", "Effectiveness checks", "Escalations", "Potential regulatory or customer notifications"],
  },
  {
    title: "Prevent",
    text: "Structural changes to keep control: improved event classification, extension controls, CAPA effectiveness criteria and management dashboards, plus root cause investigation and writing training for your teams.",
    note: "A mock inspection verifies that you can defend your records.",
  },
];

const WHY = [
  { title: "Experts matched to the issue", text: "Investigation and CAPA experts matched to the technical issue, with on-site support where needed." },
  { title: "QA and specialist expertise", text: "Relevant QA and specialist expertise for deviations, complaints and audit or inspection findings." },
  { title: "A field-tested approach", text: "Built on hands-on experience supporting organizations through remediation and inspection preparation." },
  { title: "Flexible and fast", text: "On-site or remote delivery, with a quick response when a backlog or an inspection creates urgency." },
];


const InvestigationsCapaManagement = () => {
  const base = getServiceBySlug("quality-assurance");
  if (!base) return <Navigate to="/en/services" replace />;

  const service: ServiceData = {
    ...base,
    title: "Quality Compliance, Remediation & CAPA Management",
    subtitle: "From backlog to control",
    heroImage: heroImg,
    description:
      "We support the full investigation and CAPA process, from intake and triage of deviations, complaints and audit or inspection findings to root cause analysis, action planning, execution support and effectiveness review. When a backlog has built up, we help you bring it under control, and build the maturity to keep it that way.",
  };

  return (
    <>
      <SEOHead title={CAPA_META_TITLE} description={CAPA_META_DESCRIPTION} ogImage={CAPA_HERO_IMAGE} canonical={`https://qbdgroup.com${CAPA_PATH}`} />
      <BreadcrumbJsonLd
        items={[
          { name: "Services", path: "/en/services" },
          { name: "Quality Assurance", path: "/en/services/quality-assurance" },
          { name: "Quality Compliance, Remediation & CAPA Management" },
        ]}
      />
      <ScrollToTop />
      <Navbar />
      <CommercialBrowseSidebar />

      <main className="pb-16 sm:pb-0" style={{ "--service-color": service.color } as React.CSSProperties}>
        <ServiceHero
          eyebrow="Quality Assurance"
          service={service}
          subtitleClassName="text-xl sm:text-2xl"
          parentCrumbs={[{ label: "Quality Assurance", to: "/en/services/quality-assurance" }]}
          discoverButtonTarget="our-approach"
          discoverButtonLabel="Discover our approach"
          discoverButtonIcon={ArrowDown}
          onCtaClick={() => { window.location.href = "/en/contact"; }}
          primaryCtaLabel="Get in touch"
        />

        {/* The challenge */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
            <div className="grid gap-10 md:grid-cols-2 md:items-center">
              <div>
                <Eyebrow>The challenge</Eyebrow>
                <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-6">Why investigations and CAPAs are an inspection risk</h2>
                <p className="text-lg text-qa-copy-body leading-[1.6]">
                  Overdue investigations and CAPAs are among the most cited observations during regulatory inspections. Root cause investigations and CAPAs typically come on top of the daily workload of manufacturing personnel, which makes them a recurring compliance risk across the industry. A growing backlog rarely remains an internal quality issue. When inspectors arrive, it is often the first sign that a quality system needs closer attention, and it undermines an organization's ability to defend its records.
                </p>
              </div>
              <img src={challengeImg} alt="A cross-functional team of QA, production and engineering staff reviewing a deviation on a tablet at a production line" loading="lazy" width={1200} height={912} className="w-full aspect-[4/3] object-cover rounded-[16px]" />
            </div>
            <div className="mt-12 rounded-[16px] bg-qa-copy-accent/15 p-7 md:p-10">
              <div className="flex items-center gap-2.5 border-b border-qa-copy-accent/25 pb-5 mb-7">
                <AlertTriangle size={22} strokeWidth={1.75} aria-hidden="true" className="text-qa-copy-accent-strong" />
                <p className="text-sm sm:text-base font-bold uppercase tracking-[0.14em] text-qa-copy-ink">Warning signs</p>
              </div>
              <ul className="grid gap-x-8 gap-y-5 grid-cols-2 lg:grid-cols-4">
                {WARNING_SIGNS.map((w) => (
                  <li key={w} className="flex items-start gap-3 text-sm font-medium text-qa-copy-ink leading-snug">
                    <span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-qa-copy-accent-strong" />
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* How we help */}
        <section className="py-16 md:py-24 bg-qa-copy-surface">
          <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
            <Eyebrow>Our services</Eyebrow>
            <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-8">How we help</h2>
            <p className="max-w-[800px] py-2 text-xl sm:text-[22px] font-medium leading-[1.5] text-qa-copy-ink mb-12">
              Our focus is on CAPA effectiveness rather than CAPA closure: reducing the backlog without compromising investigation quality, and making sure every action actually addresses its root cause.
            </p>
            <div className="grid gap-6 md:grid-cols-3">
              {CARDS.map(({ icon: Icon, title, img, alt, text }) => (
                <article key={title} className="flex h-full flex-col overflow-hidden rounded-[16px] border border-qa-copy-border/50 bg-card shadow-sm">
                  <img src={img} alt={alt} loading="lazy" width={1280} height={720} className="w-full aspect-video object-cover" />
                  <div className="flex flex-1 flex-col p-8">
                    <Icon size={28} strokeWidth={1.5} aria-hidden="true" className="mb-3 text-qa-copy-accent" />
                    <h3 className="text-lg font-semibold text-foreground mb-3">{title}</h3>
                    <p className="text-sm text-qa-copy-body leading-relaxed">{text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Our approach */}
        <section id="our-approach" className="scroll-mt-28 py-16 md:py-24 bg-background">
          <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
            <Eyebrow>Our approach</Eyebrow>
            <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-10">Stabilize, reduce, prevent</h2>
            <img src={approachImg} alt="A root cause analysis workshop with a team discussing a fishbone diagram on a whiteboard" loading="lazy" width={1920} height={640} className="w-full aspect-[16/9] md:aspect-[3/1] object-cover rounded-[16px] mb-12" />
            <ol className="grid gap-6 md:grid-cols-3 md:gap-10">
              {PHASES.map((p, i) => (
                <li key={p.title} className="relative flex h-full flex-col rounded-[16px] border border-qa-copy-border/50 bg-card p-6 md:p-8 shadow-sm">
                  <div className="mb-5 flex items-baseline justify-end gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-qa-copy-accent-strong">Step</span>
                    <span className="text-3xl font-bold leading-none text-qa-copy-accent-strong">{i + 1}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">{p.title}</h3>
                  <p className="text-sm text-qa-copy-body leading-relaxed mb-5">{p.text}</p>
                  {p.tags && (
                    <ul className="space-y-2">
                      {p.tags.map((t) => (
                        <li key={t} className="flex items-start gap-2.5 text-sm text-qa-copy-body leading-relaxed">
                          <span aria-hidden="true" className="mt-[7px] size-1.5 shrink-0 rounded-full bg-qa-copy-accent" />
                          <span>{t}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {p.note && (
                    <div className="mt-auto flex gap-3 rounded-[12px] bg-qa-copy-accent/15 p-5">
                      <ShieldCheck size={20} strokeWidth={1.75} aria-hidden="true" className="shrink-0 text-qa-copy-accent-strong" />
                      <p className="text-sm font-medium text-qa-copy-ink leading-relaxed">{p.note}</p>
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Upcoming webinar: single tinted surface, key visual clipped by the outer corners */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
            <div className="grid overflow-hidden rounded-[16px] bg-qa-copy-accent/[0.12] md:grid-cols-[2fr_3fr]">
              <img
                src={WEBINAR_IMAGE}
                alt="A presenter speaking to a camera in a studio-style webinar setting"
                loading="lazy"
                width={1200}
                height={675}
                className="w-full h-full aspect-video md:aspect-auto object-cover"
              />
              <div className="flex flex-col justify-center p-6 md:p-12">
                <Eyebrow>Upcoming webinar</Eyebrow>
                <h2 className="text-xl sm:text-2xl font-semibold text-foreground mb-2">From Backlog to Control</h2>
                <p className="text-base text-qa-copy-body leading-relaxed mb-4">A practical framework to regain control of your CAPA backlog</p>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-qa-copy-body mb-5">
                  <span className="inline-flex items-center gap-2"><CalendarDays className="h-4 w-4 text-qa-copy-accent" aria-hidden="true" />1 December 2026, 3 PM CET</span>
                  <span className="inline-flex items-center gap-2"><Globe className="h-4 w-4 text-qa-copy-accent" aria-hidden="true" />English</span>
                </div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex shrink-0">
                    {WEBINAR_SPEAKERS.map((s, i) => (
                      <img key={s.name} src={s.img} alt={s.name} loading="lazy" width={44} height={44} className={cn("h-11 w-11 rounded-full border-2 border-background object-cover bg-muted", i > 0 && "-ml-3")} />
                    ))}
                  </div>
                  <p className="text-sm text-qa-copy-body">With Björn Aalbers, Monique Eilander and Bas Michielsen</p>
                </div>
                <div>
                  <Button asChild size="lg" className="rounded-full bg-qa-copy-ink text-primary-foreground hover:bg-qa-copy-ink/90">
                    <Link to={WEBINAR_PATH}>Register now<ArrowRight aria-hidden="true" /></Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* Why QbD Group (no imagery by rule) */}
        <section className="py-16 md:py-24 bg-qa-copy-ink">
          <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
            <div className="mb-12">
              <Eyebrow light>Your partner</Eyebrow>
              <h2 className="text-2xl sm:text-3xl font-semibold text-primary-foreground mb-6">Why choose QbD Group for investigations &amp; CAPA?</h2>
              <p className="max-w-[760px] text-lg text-primary-foreground/75 leading-[1.6] mb-8">With a 20+ year track record as an investigation and CAPA partner, QbD Group is a preferred supplier for Top 20 Pharma and MedTech companies.</p>
              <Button asChild size="lg" className="rounded-full bg-qa-copy-accent text-qa-copy-ink hover:bg-qa-copy-accent/90">
                <Link to="/en/contact">Partner with us<ArrowRight aria-hidden="true" /></Link>
              </Button>
            </div>
            <div className="grid gap-6 auto-rows-fr sm:grid-cols-2">
              {WHY.map((w) => (
                <div key={w.title} className="flex flex-col rounded-[16px] border border-primary-foreground/10 bg-primary-foreground/5 p-6">
                  <CheckCircle2 size={22} strokeWidth={1.5} aria-hidden="true" className="text-qa-copy-accent mb-3" />
                  <h3 className="text-base font-semibold text-primary-foreground mb-2">{w.title}</h3>
                  <p className="text-sm text-primary-foreground/70 leading-relaxed">{w.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ContactCTA title={base.formHeading} description={base.formSubtitle} ctaLabel="Contact us" />
      </main>
      <Footer />
    </>
  );
};

export default InvestigationsCapaManagement;
